import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ChevronDown,
  ChevronRight,
  Rocket,
  Map,
  History,
  Bell,
  Target,
  FileText,
  Users,
  Settings,
  HelpCircle,
} from 'lucide-react';
import clsx from 'clsx';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqSection {
  title: string;
  icon: React.ElementType;
  color: string;
  items: FaqItem[];
}

// FAQ structure: titles + Q/A keys are resolved with i18n at render time so
// the page tracks the user's language. Adding a new entry = add the key to
// fr/en/de.json and append it here.
const FAQ_DEFINITION: Array<{
  titleKey: string;
  icon: React.ElementType;
  color: string;
  items: Array<{ qKey: string; aKey: string }>;
}> = [
  {
    titleKey: 'help.section_first_steps',
    icon: Rocket,
    color: 'text-green-600 bg-green-50',
    items: [
      { qKey: 'help.faq_first_vehicle_q', aKey: 'help.faq_first_vehicle_a' },
      { qKey: 'help.faq_tracker_q', aKey: 'help.faq_tracker_a' },
      { qKey: 'help.faq_compatible_q', aKey: 'help.faq_compatible_a' },
      { qKey: 'help.faq_invite_users_q', aKey: 'help.faq_invite_users_a' },
    ],
  },
  {
    titleKey: 'help.section_map',
    icon: Map,
    color: 'text-blue-600 bg-blue-50',
    items: [
      { qKey: 'help.faq_see_vehicles_q', aKey: 'help.faq_see_vehicles_a' },
      { qKey: 'help.faq_filter_vehicles_q', aKey: 'help.faq_filter_vehicles_a' },
      { qKey: 'help.faq_share_position_q', aKey: 'help.faq_share_position_a' },
      { qKey: 'help.faq_realtime_q', aKey: 'help.faq_realtime_a' },
    ],
  },
  {
    titleKey: 'help.section_history',
    icon: History,
    color: 'text-purple-600 bg-purple-50',
    items: [
      { qKey: 'help.faq_history_view_q', aKey: 'help.faq_history_view_a' },
      { qKey: 'help.faq_history_retention_q', aKey: 'help.faq_history_retention_a' },
      { qKey: 'help.faq_export_trip_q', aKey: 'help.faq_export_trip_a' },
    ],
  },
  {
    titleKey: 'help.section_alerts',
    icon: Bell,
    color: 'text-red-600 bg-red-50',
    items: [
      { qKey: 'help.faq_speed_alert_q', aKey: 'help.faq_speed_alert_a' },
      { qKey: 'help.faq_alert_types_q', aKey: 'help.faq_alert_types_a' },
      { qKey: 'help.faq_disable_alert_q', aKey: 'help.faq_disable_alert_a' },
      { qKey: 'help.faq_cooldown_q', aKey: 'help.faq_cooldown_a' },
    ],
  },
  {
    titleKey: 'help.section_geofences',
    icon: Target,
    color: 'text-orange-600 bg-orange-50',
    items: [
      { qKey: 'help.faq_create_zone_q', aKey: 'help.faq_create_zone_a' },
      { qKey: 'help.faq_zone_types_q', aKey: 'help.faq_zone_types_a' },
      { qKey: 'help.faq_zone_alert_q', aKey: 'help.faq_zone_alert_a' },
    ],
  },
  {
    titleKey: 'help.section_reports',
    icon: FileText,
    color: 'text-cyan-600 bg-cyan-50',
    items: [
      { qKey: 'help.faq_reports_types_q', aKey: 'help.faq_reports_types_a' },
      { qKey: 'help.faq_schedule_report_q', aKey: 'help.faq_schedule_report_a' },
      { qKey: 'help.faq_export_excel_q', aKey: 'help.faq_export_excel_a' },
      { qKey: 'help.faq_fuel_q', aKey: 'help.faq_fuel_a' },
    ],
  },
  {
    titleKey: 'help.section_drivers',
    icon: Users,
    color: 'text-indigo-600 bg-indigo-50',
    items: [
      { qKey: 'help.faq_assign_driver_q', aKey: 'help.faq_assign_driver_a' },
      { qKey: 'help.faq_scoring_q', aKey: 'help.faq_scoring_a' },
      { qKey: 'help.faq_score_evolution_q', aKey: 'help.faq_score_evolution_a' },
    ],
  },
  {
    titleKey: 'help.section_admin',
    icon: Settings,
    color: 'text-gray-600 bg-gray-50',
    items: [
      { qKey: 'help.faq_change_password_q', aKey: 'help.faq_change_password_a' },
      { qKey: 'help.faq_roles_q', aKey: 'help.faq_roles_a' },
      { qKey: 'help.faq_white_label_q', aKey: 'help.faq_white_label_a' },
      { qKey: 'help.faq_export_data_q', aKey: 'help.faq_export_data_a' },
      { qKey: 'help.faq_delete_account_q', aKey: 'help.faq_delete_account_a' },
    ],
  },
];

export default function HelpPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  // Resolve FAQ titles + Q/A in the current language. Recomputed each render
  // so language switches reflow without remounting.
  const faqSections: FaqSection[] = useMemo(
    () =>
      FAQ_DEFINITION.map((s) => ({
        title: t(s.titleKey),
        icon: s.icon,
        color: s.color,
        items: s.items.map((it) => ({ question: t(it.qKey), answer: t(it.aKey) })),
      })),
    [t],
  );

  const toggleSection = (index: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleItem = (key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return faqSections;

    const query = searchQuery.toLowerCase();
    return faqSections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.question.toLowerCase().includes(query) ||
            item.answer.toLowerCase().includes(query)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [searchQuery, faqSections]);

  const totalResults = filteredSections.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <HelpCircle className="h-8 w-8 text-green-600" />
          <h1 className="text-2xl font-bold text-gray-900">{t('help.title')}</h1>
        </div>
        <p className="text-gray-500">{t('help.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('help.search_placeholder')}
          className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/20 transition-all text-base"
        />
        {searchQuery && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
            {t('help.results_count', { count: totalResults })}
          </span>
        )}
      </div>

      {/* FAQ Sections */}
      <div className="space-y-4">
        {filteredSections.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg">{t('help.no_results_for', { query: searchQuery })}</p>
            <p className="text-gray-400 text-sm mt-1">{t('help.try_other_keywords')}</p>
          </div>
        )}

        {filteredSections.map((section, sectionIndex) => {
          const isOpen = searchQuery ? true : openSections.has(sectionIndex);
          const Icon = section.icon;

          return (
            <div
              key={section.title}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              {/* Section header */}
              <button
                onClick={() => toggleSection(sectionIndex)}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <div
                  className={clsx(
                    'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                    section.color
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-base font-semibold text-gray-900 flex-1 text-left">
                  {section.title}
                </span>
                <span className="text-xs text-gray-400 mr-2">
                  {t('help.articles_count', { count: section.items.length })}
                </span>
                <ChevronDown
                  className={clsx(
                    'h-5 w-5 text-gray-400 transition-transform',
                    isOpen && 'rotate-180'
                  )}
                />
              </button>

              {/* Section items */}
              {isOpen && (
                <div className="border-t border-gray-100">
                  {section.items.map((item, itemIndex) => {
                    const key = `${sectionIndex}-${itemIndex}`;
                    const isItemOpen = searchQuery ? true : openItems.has(key);

                    return (
                      <div
                        key={key}
                        className={clsx(
                          'border-b border-gray-50 last:border-b-0',
                          isItemOpen && 'bg-gray-50/50'
                        )}
                      >
                        <button
                          onClick={() => toggleItem(key)}
                          className="w-full flex items-start gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left"
                        >
                          <ChevronRight
                            className={clsx(
                              'h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0 transition-transform',
                              isItemOpen && 'rotate-90'
                            )}
                          />
                          <span
                            className={clsx(
                              'text-sm font-medium',
                              isItemOpen ? 'text-gray-900' : 'text-gray-700'
                            )}
                          >
                            {item.question}
                          </span>
                        </button>
                        {isItemOpen && (
                          <div className="px-5 pb-4 pl-12">
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Contact */}
      <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-cyan-50 rounded-xl border border-green-200">
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          {t('help.contact_title')}
        </h3>
        <p className="text-sm text-gray-600">
          {t('help.contact_body_before')}{' '}
          <a
            href="mailto:support@trackfleet.lu"
            className="text-green-600 font-medium hover:underline"
          >
            support@trackfleet.lu
          </a>{' '}
          {t('help.contact_body_after')}
        </p>
      </div>
    </div>
  );
}
